import {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_RowData,
  MRT_SortingState,
  MRT_TableInstance,
  MRT_Updater,
} from 'material-react-table'
import { useCallback, useEffect, useRef, useState } from 'react'

type UseTomTableStatePersistenceProps<TData extends MRT_RowData> = {
  table: MRT_TableInstance<TData>
  tableName?: string
  keepPaginationState?: boolean
  keepColumnFiltersState?: boolean
  keepColumnSortingState?: boolean
}

type ColumnFilterFnsState = Record<string, string>
type TableMemoryState = {
  pagination?: MRT_PaginationState
  columnFilters?: MRT_ColumnFiltersState
  columnFilterFns?: ColumnFilterFnsState
  sorting?: MRT_SortingState
}

const tableStateMemory = new Map<string, TableMemoryState>()
const SORTING_MOUNT_RESET_GUARD_MS = 1500

const areColumnFilterFnsEqual = (left: ColumnFilterFnsState, right: ColumnFilterFnsState) => {
  const leftKeys = Object.keys(left)
  const rightKeys = Object.keys(right)

  if (leftKeys.length !== rightKeys.length) {
    return false
  }

  return leftKeys.every(key => left[key] === right[key])
}

const pickColumnFilterFnsByIds = (filterFns: ColumnFilterFnsState, columnIds: Set<string>) => {
  const picked: ColumnFilterFnsState = {}

  columnIds.forEach(columnId => {
    if (columnId in filterFns) {
      picked[columnId] = filterFns[columnId]
    }
  })

  return picked
}

export const useTomTableStatePersistence = <TData extends MRT_RowData>(
  props: UseTomTableStatePersistenceProps<TData>,
) => {
  const { table, keepPaginationState, keepColumnFiltersState, keepColumnSortingState, tableName } =
    props

  const memoryKey = tableName
    ? `${window.location.pathname}_${tableName}`
    : `${window.location.pathname}_TomTable`

  const defaultPaginationRef = useRef<MRT_PaginationState>(
    table.getState().pagination ?? { pageIndex: 0, pageSize: 10 },
  )
  const defaultColumnFiltersRef = useRef<MRT_ColumnFiltersState>(
    table.getState().columnFilters ?? [],
  )
  const defaultColumnFilterFnsRef = useRef<ColumnFilterFnsState>(
    (table.getState().columnFilterFns as ColumnFilterFnsState) ?? {},
  )
  const defaultSortingRef = useRef<MRT_SortingState>(table.getState().sorting ?? [])
  const defaultPagination = defaultPaginationRef.current
  const defaultColumnFilters = defaultColumnFiltersRef.current
  const defaultColumnFilterFns = defaultColumnFilterFnsRef.current
  const defaultSorting = defaultSortingRef.current
  const rowCount = table.getPrePaginationRowModel?.().rows.length ?? 0

  const normalizeColumnFilters = useCallback(
    (filters: MRT_ColumnFiltersState): MRT_ColumnFiltersState => {
      const getOptionValue = (option: unknown) => {
        if (typeof option === 'object' && option !== null && 'value' in option) {
          return (option as { value: unknown }).value
        }

        return option
      }

      return filters.reduce<MRT_ColumnFiltersState>((acc, filter) => {
        const column = table.getColumn(filter.id)
        if (!column) {
          return acc
        }

        const columnDef = column.columnDef as {
          filterVariant?: string
          filterSelectOptions?: Array<string | { label?: string; value: unknown }>
        }
        const variant = columnDef.filterVariant
        const selectOptions = columnDef.filterSelectOptions

        if (
          (variant === 'select' || variant === 'multi-select') &&
          Array.isArray(selectOptions) &&
          selectOptions.length > 0
        ) {
          const optionValues = new Set(selectOptions.map(option => getOptionValue(option)))

          if (variant === 'multi-select') {
            if (!Array.isArray(filter.value)) {
              return acc
            }

            const filteredValue = filter.value
              .map(value => getOptionValue(value))
              .filter(value => optionValues.has(value))
            if (filteredValue.length <= 0) {
              return acc
            }

            acc.push({ ...filter, value: filteredValue })
            return acc
          }

          const normalizedValue = getOptionValue(filter.value)
          if (!optionValues.has(normalizedValue)) {
            return acc
          }

          acc.push({ ...filter, value: normalizedValue })
          return acc
        }

        acc.push(filter)
        return acc
      }, [])
    },
    [table],
  )

  const [pagination, setPagination] = useState<MRT_PaginationState>(() => {
    if (!keepPaginationState) {
      return defaultPagination
    }

    return (tableStateMemory.get(memoryKey) ?? {}).pagination ?? defaultPagination
  })
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(() => {
    if (!keepColumnFiltersState) {
      return defaultColumnFilters
    }

    const saved = (tableStateMemory.get(memoryKey) ?? {}).columnFilters ?? defaultColumnFilters
    return normalizeColumnFilters(saved)
  })
  const [columnFilterFns, setColumnFilterFns] = useState<ColumnFilterFnsState>(() => {
    if (!keepColumnFiltersState) {
      return defaultColumnFilterFns
    }

    return (tableStateMemory.get(memoryKey) ?? {}).columnFilterFns ?? defaultColumnFilterFns
  })
  const [sorting, setSorting] = useState<MRT_SortingState>(() => {
    if (!keepColumnSortingState) {
      return defaultSorting
    }

    return (tableStateMemory.get(memoryKey) ?? {}).sorting ?? defaultSorting
  })
  // Track whether sorting was restored on initial mount only.
  // This guard must not be re-armed after user-driven sorting changes.
  const sortingMountAtRef = useRef(Date.now())
  const hadPersistedSortingOnMountRef = useRef(!!keepColumnSortingState && sorting.length > 0)

  const clampPaginationToData = useCallback(
    (paginationState: MRT_PaginationState) => {
      const pageCount = Math.max(1, Math.ceil(rowCount / (paginationState.pageSize || 1)))
      const maxPageIndex = pageCount - 1
      if (paginationState.pageIndex > maxPageIndex) {
        return { ...paginationState, pageIndex: maxPageIndex }
      }

      return paginationState
    },
    [rowCount],
  )

  const paginationRef = useRef(pagination)
  // Keep a live ref of pagination so wrapped table setters always read latest state.
  useEffect(() => {
    paginationRef.current = clampPaginationToData(pagination)
  }, [clampPaginationToData, pagination])

  // Holds the unwrapped MRT setPagination so Effect 2 can push state without
  // going through the wrapper (which would double-fire onPaginationChange).
  const originalSetPaginationRef = useRef(table.setPagination)

  const columnFiltersRef = useRef(columnFilters)
  // Keep a live ref of column filters to avoid stale closures during table callbacks.
  useEffect(() => {
    columnFiltersRef.current = columnFilters
  }, [columnFilters])

  const columnFilterFnsRef = useRef(columnFilterFns)
  // Keep a live ref of filter functions for stable reads in wrapped update handlers.
  useEffect(() => {
    columnFilterFnsRef.current = columnFilterFns
  }, [columnFilterFns])

  const sortingRef = useRef(sorting)
  // Keep a live ref of sorting state for stable reads in wrapped update handlers.
  useEffect(() => {
    sortingRef.current = sorting
  }, [sorting])

  // Persist pagination into in-memory cache whenever it changes.
  useEffect(() => {
    if (!keepPaginationState) {
      return
    }

    tableStateMemory.set(memoryKey, {
      ...(tableStateMemory.get(memoryKey) ?? {}),
      pagination,
    })
  }, [keepPaginationState, memoryKey, pagination])

  // Persist column filters and filter modes into in-memory cache whenever they change.
  useEffect(() => {
    if (!keepColumnFiltersState) {
      return
    }

    tableStateMemory.set(memoryKey, {
      ...(tableStateMemory.get(memoryKey) ?? {}),
      columnFilters,
      columnFilterFns,
    })
  }, [columnFilterFns, columnFilters, keepColumnFiltersState, memoryKey])

  // Persist sorting into in-memory cache whenever it changes.
  useEffect(() => {
    if (!keepColumnSortingState) {
      return
    }

    tableStateMemory.set(memoryKey, {
      ...(tableStateMemory.get(memoryKey) ?? {}),
      sorting,
    })
  }, [keepColumnSortingState, memoryKey, sorting])

  // On unmount, store the latest table state as a fallback snapshot.
  useEffect(() => {
    return () => {
      const latestState = table.getState()
      const previousMemory = tableStateMemory.get(memoryKey) ?? {}

      tableStateMemory.set(memoryKey, {
        ...previousMemory,
        pagination: keepPaginationState
          ? ((latestState.pagination as MRT_PaginationState) ?? paginationRef.current)
          : previousMemory.pagination,
        columnFilters: keepColumnFiltersState
          ? normalizeColumnFilters(
              (latestState.columnFilters as MRT_ColumnFiltersState) ?? columnFiltersRef.current,
            )
          : previousMemory.columnFilters,
        columnFilterFns: keepColumnFiltersState
          ? ((latestState.columnFilterFns as ColumnFilterFnsState) ?? columnFilterFnsRef.current)
          : previousMemory.columnFilterFns,
        sorting: keepColumnSortingState
          ? ((latestState.sorting as MRT_SortingState) ?? sortingRef.current)
          : previousMemory.sorting,
      })
    }
  }, [
    keepColumnFiltersState,
    keepColumnSortingState,
    keepPaginationState,
    memoryKey,
    normalizeColumnFilters,
    table,
  ])

  // Wrap pagination setters so UI changes update React state and MRT state in sync.
  useEffect(() => {
    if (!keepPaginationState) {
      return
    }

    const originalSetPagination = table.setPagination
    originalSetPaginationRef.current = originalSetPagination
    const originalSetPageIndex = table.setPageIndex
    const originalSetPageSize = table.setPageSize
    const originalOnPaginationChange = table.options.onPaginationChange
    const applyPaginationUpdate = (
      updater: MRT_PaginationState | ((prev: MRT_PaginationState) => MRT_PaginationState),
    ) => {
      const next = typeof updater === 'function' ? updater(paginationRef.current) : updater
      const clamped = clampPaginationToData(next)
      setPagination(clamped)
      originalOnPaginationChange?.(clamped)
      originalSetPagination(clamped)
    }

    table.setPagination = applyPaginationUpdate
    table.setPageIndex = updater =>
      applyPaginationUpdate(prev => ({
        ...prev,
        pageIndex: typeof updater === 'function' ? updater(prev.pageIndex) : updater,
      }))
    table.setPageSize = updater =>
      applyPaginationUpdate(prev => ({
        ...prev,
        pageSize: typeof updater === 'function' ? updater(prev.pageSize) : updater,
      }))

    return () => {
      table.setPagination = originalSetPagination
      table.setPageIndex = originalSetPageIndex
      table.setPageSize = originalSetPageSize
    }
  }, [clampPaginationToData, keepPaginationState, table])

  // Wrap column filter setters so filter values/modes are normalized and synchronized.
  useEffect(() => {
    if (!keepColumnFiltersState) {
      return
    }

    const originalAutoResetPageIndex = table.options.autoResetPageIndex
    table.options.autoResetPageIndex = false

    const originalSetColumnFilters = table.setColumnFilters
    const originalSetColumnFilterFns = table.setColumnFilterFns
    const originalOnColumnFiltersChange = table.options.onColumnFiltersChange
    const originalOnColumnFilterFnsChange = table.options.onColumnFilterFnsChange
    const applyColumnFiltersUpdate = (
      updater: MRT_ColumnFiltersState | MRT_Updater<MRT_ColumnFiltersState>,
    ) => {
      const nextFilters =
        typeof updater === 'function' ? updater(columnFiltersRef.current) : updater
      const normalizedFilters = normalizeColumnFilters(nextFilters)
      columnFiltersRef.current = normalizedFilters
      setColumnFilters(normalizedFilters)
      originalOnColumnFiltersChange?.(normalizedFilters)
      originalSetColumnFilters(normalizedFilters)
    }
    const applyColumnFilterFnsUpdate = (
      updater: ColumnFilterFnsState | MRT_Updater<ColumnFilterFnsState>,
    ) => {
      const nextFilterFns =
        typeof updater === 'function' ? updater(columnFilterFnsRef.current) : updater
      columnFilterFnsRef.current = nextFilterFns
      setColumnFilterFns(nextFilterFns)
      originalOnColumnFilterFnsChange?.(nextFilterFns)
      originalSetColumnFilterFns(nextFilterFns)
    }

    table.setColumnFilters = applyColumnFiltersUpdate
    table.setColumnFilterFns = applyColumnFilterFnsUpdate
    originalSetColumnFilters(normalizeColumnFilters(columnFiltersRef.current))
    originalSetColumnFilterFns(columnFilterFnsRef.current)

    return () => {
      table.options.autoResetPageIndex = originalAutoResetPageIndex
      table.setColumnFilters = originalSetColumnFilters
      table.setColumnFilterFns = originalSetColumnFilterFns
    }
  }, [keepColumnFiltersState, normalizeColumnFilters, table])

  // Wrap sorting setter so UI changes update React state and MRT state in sync.
  useEffect(() => {
    if (!keepColumnSortingState) {
      return
    }

    const originalSetSorting = table.setSorting
    const originalOnSortingChange = table.options.onSortingChange
    const applySortingUpdate = (updater: MRT_SortingState | MRT_Updater<MRT_SortingState>) => {
      const nextSorting = typeof updater === 'function' ? updater(sortingRef.current) : updater
      // Some table lifecycles trigger an immediate empty-sort update after mount.
      // If we restored a persisted sort, ignore that first transient reset.
      const isMountResetToDefaultOrEmpty =
        nextSorting.length === 0 ||
        JSON.stringify(nextSorting) === JSON.stringify(defaultSortingRef.current)

      const shouldIgnoreMountReset =
        hadPersistedSortingOnMountRef.current &&
        sortingRef.current.length > 0 &&
        isMountResetToDefaultOrEmpty &&
        Date.now() - sortingMountAtRef.current < SORTING_MOUNT_RESET_GUARD_MS

      if (shouldIgnoreMountReset) {
        return
      }

      sortingRef.current = nextSorting
      setSorting(nextSorting)
      originalOnSortingChange?.(nextSorting)
      originalSetSorting(nextSorting)
    }

    table.setSorting = applySortingUpdate
    originalSetSorting(sortingRef.current)

    return () => {
      table.setSorting = originalSetSorting
    }
  }, [keepColumnSortingState, table])

  // Clamp stored pagination when data size changes (e.g. filters reduce available pages).
  useEffect(() => {
    if (!keepPaginationState) {
      return
    }

    const clamped = clampPaginationToData(pagination)
    if (clamped.pageIndex !== pagination.pageIndex || clamped.pageSize !== pagination.pageSize) {
      setPagination(clamped)
    }
  }, [clampPaginationToData, keepPaginationState, pagination])

  // Push current pagination into MRT table state after any local pagination update.
  // Uses the unwrapped setter directly to avoid re-triggering the wrapper and
  // double-firing onPaginationChange.
  useEffect(() => {
    if (!keepPaginationState) {
      return
    }

    const clamped = clampPaginationToData(pagination)
    originalSetPaginationRef.current(clamped)
    if (clamped.pageIndex !== pagination.pageIndex || clamped.pageSize !== pagination.pageSize) {
      setPagination(clamped)
    }
  }, [clampPaginationToData, keepPaginationState, pagination])

  const hasPersistedPagination =
    !!keepPaginationState &&
    (pagination.pageIndex !== defaultPagination.pageIndex ||
      pagination.pageSize !== defaultPagination.pageSize)
  const hasPersistedColumnFilters =
    !!keepColumnFiltersState &&
    JSON.stringify(columnFilters) !== JSON.stringify(defaultColumnFilters)
  const relevantFilterIds = new Set([
    ...columnFilters.map(filter => filter.id),
    ...defaultColumnFilters.map(filter => filter.id),
  ])
  const relevantColumnFilterFns = pickColumnFilterFnsByIds(columnFilterFns, relevantFilterIds)
  const relevantDefaultColumnFilterFns = pickColumnFilterFnsByIds(
    defaultColumnFilterFns,
    relevantFilterIds,
  )
  const hasPersistedColumnFilterModes =
    !!keepColumnFiltersState &&
    relevantFilterIds.size > 0 &&
    !areColumnFilterFnsEqual(relevantColumnFilterFns, relevantDefaultColumnFilterFns)
  const hasPersistedSorting =
    !!keepColumnSortingState &&
    sorting.length > 0 &&
    JSON.stringify(sorting) !== JSON.stringify(defaultSorting)

  const hasPersistedTableState =
    hasPersistedPagination ||
    hasPersistedColumnFilters ||
    hasPersistedColumnFilterModes ||
    hasPersistedSorting

  const onResetTableState = useCallback(() => {
    if (keepPaginationState) {
      setPagination(defaultPagination)
      paginationRef.current = defaultPagination
    }

    if (keepColumnFiltersState) {
      table.setColumnFilters(defaultColumnFilters)
      table.setColumnFilterFns(defaultColumnFilterFns)
      table.setGlobalFilter(undefined)
    }

    if (keepColumnSortingState) {
      // Disarm the mount guard so this explicit reset is never swallowed.
      hadPersistedSortingOnMountRef.current = false
      table.setSorting(defaultSorting)
      sortingRef.current = defaultSorting
    }

    const previousMemory = tableStateMemory.get(memoryKey) ?? {}
    tableStateMemory.set(memoryKey, {
      ...previousMemory,
      pagination: keepPaginationState ? defaultPagination : previousMemory.pagination,
      columnFilters: keepColumnFiltersState ? defaultColumnFilters : previousMemory.columnFilters,
      columnFilterFns: keepColumnFiltersState
        ? defaultColumnFilterFns
        : previousMemory.columnFilterFns,
      sorting: keepColumnSortingState ? defaultSorting : previousMemory.sorting,
    })
  }, [
    defaultColumnFilterFns,
    defaultColumnFilters,
    defaultPagination,
    defaultSorting,
    keepColumnFiltersState,
    keepColumnSortingState,
    keepPaginationState,
    memoryKey,
    table,
  ])

  return { hasPersistedTableState, onResetTableState }
}
